/**
 * To do — the Class Creator's first page (Design's sketch "the split", Nino's go 27 Oct): the
 * classes and drills that need work before they can go live, moved here from the Command
 * Centre's Today.
 *
 *   - Four counts: Drafts, Waiting for review, Missing Bahasa, No picture yet. The last two have
 *     no data on the server yet, so they say "Not available yet" and count nothing.
 *   - One list, "Need work": each piece of work once (a class waiting for review is not also a
 *     draft), a chip for why, the first six, then "See all N ›".
 *   - A list that can't load says so; it never shows as zero.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@/components/shared';
import { cn } from '@/lib/utils';
import { contentStoreApi, microClassApi, skillDrillBuilderApi } from '@/lib/api';
import type { ClassRow } from '@/lib/classStatus';
import { line, workList, type DrillRow } from '@/lib/todo';
import type { PendingContentItem } from '@/types/command-centre';

/** To do is a quick look: four rows, then "See all N ›" (Design's sketch). */
const FIRST = 4;

export default function ToDo() {
  const pending = useQuery({
    queryKey: ['content-pending-list'],
    queryFn: async () => (((await contentStoreApi.pending({ limit: 100 })) as { items?: PendingContentItem[] }).items ?? []),
    retry: false,
  });
  const classes = useQuery({
    queryKey: ['micro-classes-drafts'],
    queryFn: () => microClassApi.listDrafts(),
    select: (data: unknown) => (Array.isArray(data) ? (data as ClassRow[]) : []),
    retry: false,
  });
  const drills = useQuery({
    queryKey: ['todo-drill-drafts'],
    queryFn: async () => (((await skillDrillBuilderApi.list({ limit: 500 })) as { items?: DrillRow[] }).items ?? []),
    retry: false,
  });
  const [all, setAll] = useState(false);

  const loading = pending.isLoading || classes.isLoading || drills.isLoading;
  const failed = [pending.isError && 'waiting for review', classes.isError && 'classes', drills.isError && 'skill drills'].filter(Boolean) as string[];
  const items = useMemo(() => workList(pending.data ?? [], classes.data ?? [], drills.data ?? []), [pending.data, classes.data, drills.data]);
  const drafts = items.filter((i) => i.why === 'draft').length;
  const review = items.filter((i) => i.why === 'review').length;
  const shown = all ? items : items.slice(0, FIRST);

  return (
    <PageShell title="To do" subtitle="Classes and drills that need work before they can go live.">
      <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
        <Count label="Drafts" n={loading ? null : classes.isError || drills.isError ? 'error' : drafts} />
        <Count label="Waiting for review" n={loading ? null : pending.isError ? 'error' : review} />
        <Count label="Missing Bahasa" n="none-yet" />
        <Count label="No picture yet" n="none-yet" />
      </div>

      <section aria-labelledby="need-work" className="overflow-hidden rounded-[14px] border border-border bg-card">
        <header className="flex h-12 items-center justify-between border-b border-border px-[18px]">
          <h2 id="need-work" className="text-[12px] font-extrabold uppercase tracking-[0.14em] text-foreground">Need work</h2>
          <span className="text-[13px] text-muted-foreground">{loading ? '…' : items.length}</span>
        </header>
        {failed.length > 0 && (
          <p className="border-b border-border px-[18px] py-2.5 text-[13px] text-[#F87171]">
            Can’t load the {failed.join(' or the ')} right now, so this list may be short.
          </p>
        )}
        {loading ? (
          <p className="px-[18px] py-6 text-[13.5px] text-muted-foreground">Checking…</p>
        ) : items.length === 0 ? (
          <p className="px-[18px] py-6 text-[13.5px] text-muted-foreground">{failed.length ? 'Nothing to show yet.' : 'Nothing needs work. Nice.'}</p>
        ) : (
          <ul>
            {shown.map((it) => (
              <li key={it.key} className="flex min-h-[58px] items-center gap-3.5 border-b border-border px-[18px] py-2.5 last:border-b-0 max-md:items-start max-md:gap-3 max-md:px-3.5">
                {/* One width for every tag, so every title starts at the same place. */}
                <span className="w-[68px] flex-none">
                  <span className={cn('inline-block rounded-full border px-2.5 text-[12px] font-semibold leading-[22px]',
                    it.why === 'review' ? 'border-[#F5B544]/60 text-[#F5B544]' : 'border-border text-foreground/70')}>
                    {it.why === 'review' ? 'Review' : 'Draft'}
                  </span>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold leading-[22px] text-foreground [overflow-wrap:anywhere]">{it.title}</div>
                  <div className="text-[12px] text-muted-foreground [overflow-wrap:anywhere]">{line(it.kind, it.note)}</div>
                </div>
                <Link to={it.href} className="flex-none text-[13.5px] font-semibold leading-[22px] text-foreground/70 hover:text-foreground" aria-label={`Open ${it.title}`}>Open ›</Link>
              </li>
            ))}
          </ul>
        )}
        {!loading && items.length > FIRST && (
          <button type="button" onClick={() => setAll((a) => !a)} className="flex h-12 w-full items-center justify-center border-t border-border text-[13.5px] font-semibold text-foreground hover:bg-muted/40">
            {all ? 'Show fewer' : `See all ${items.length} ›`}
          </button>
        )}
      </section>
    </PageShell>
  );
}

function Count({ label, n }: { label: string; n: number | null | 'error' | 'none-yet' }) {
  const small = n === 'error' || n === 'none-yet';
  return (
    <div className="rounded-[14px] border border-border bg-card px-[18px] py-4" role="group" aria-label={`${label}: ${n === null ? 'checking' : n === 'error' ? 'can’t check' : n === 'none-yet' ? 'not available yet' : n}`}>
      <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className={cn('mt-1.5 font-extrabold text-foreground', small ? 'text-[13.5px] font-semibold text-muted-foreground' : 'text-[26px] leading-8')}>
        {n === null ? '…' : n === 'error' ? 'Can’t check right now' : n === 'none-yet' ? 'Not available yet' : n}
      </div>
    </div>
  );
}
